import * as React from 'react';
import { ChevronDown } from 'lucide-react';
import { cn } from './lib/utils';
import {
  DropdownMenu, DropdownMenuTrigger, DropdownMenuContent,
  DropdownMenuRadioGroup, DropdownMenuRadioItem,
  DropdownMenuCheckboxItem, DropdownMenuLabel, DropdownMenuSeparator,
} from './dropdown-menu';

/**
 * Custom dropdown select — replaces native <select> with a Radix portal-based
 * dropdown that renders below the trigger and floats above all content (z-tooltip).
 *
 * Variants:
 *   variant="default"  — light bg trigger (pages, filter bars)
 *   variant="header"   — dark bg trigger (navy header bar)
 *
 * Modes:
 *   mode="single"      — single-select, radio items (default)
 *   mode="multi"       — multi-select, checkbox items
 *
 * <NNSelect value="all" onChange={setValue} options={[{value,label}]} />
 * <NNSelect mode="multi" value={['a','b']} onChange={setValues} options={[...]} />
 */

const TRIGGER_VARIANT = {
  default: cn(
    'h-8 rounded-lg border border-nn-border bg-nn-white',
    'pl-3 pr-7 text-body-sm text-text-dark',
    'hover:border-slate focus:outline-none focus:ring-2 focus:ring-aqua/40 focus:border-aqua',
  ),
  header: cn(
    'h-8 rounded-lg border border-white/20 bg-white/10',
    'pl-3 pr-7 text-body-sm text-text-primary font-medium',
    'hover:bg-white/20 focus:outline-none focus:ring-2 focus:ring-aqua/40',
  ),
};

function NNSelect({
  value,
  onChange,
  options = [],
  label,
  placeholder = 'Select...',
  variant = 'default',
  mode = 'single',
  className,
}) {
  const [open, setOpen] = React.useState(false);

  // Resolve display label
  const displayLabel = React.useMemo(() => {
    if (mode === 'multi') {
      if (!value || value.length === 0) return placeholder;
      if (value.length === 1) return options.find((o) => o.value === value[0])?.label || value[0];
      return `${value.length} selected`;
    }
    return options.find((o) => o.value === value)?.label || placeholder;
  }, [value, options, mode, placeholder]);

  return (
    <DropdownMenu open={open} onOpenChange={setOpen}>
      <DropdownMenuTrigger asChild>
        <button
          type="button"
          aria-label={label || placeholder}
          style={{ fontSize: '12px' }}
          className={cn(
            'relative inline-flex items-center cursor-pointer transition-colors font-body',
            TRIGGER_VARIANT[variant] ?? TRIGGER_VARIANT.default,
            className,
          )}
        >
          <span className="truncate">{displayLabel}</span>
          <ChevronDown
            className={cn(
              'absolute right-2 h-3.5 w-3.5 transition-transform',
              variant === 'header' ? 'text-text-primary' : 'text-text-muted',
              open && 'rotate-180',
            )}
          />
        </button>
      </DropdownMenuTrigger>

      <DropdownMenuContent
        align="start"
        sideOffset={4}
        className="z-tooltip min-w-[var(--radix-dropdown-menu-trigger-width)] max-h-[280px] overflow-y-auto"
      >
        {label && (
          <>
            <DropdownMenuLabel>{label}</DropdownMenuLabel>
            <DropdownMenuSeparator />
          </>
        )}

        {mode === 'single' ? (
          <DropdownMenuRadioGroup value={value} onValueChange={(v) => { onChange(v); setOpen(false); }}>
            {options.map((opt) => (
              <DropdownMenuRadioItem key={opt.value} value={opt.value}>
                {opt.label}
              </DropdownMenuRadioItem>
            ))}
          </DropdownMenuRadioGroup>
        ) : (
          options.map((opt) => {
            const checked = Array.isArray(value) && value.includes(opt.value);
            return (
              <DropdownMenuCheckboxItem
                key={opt.value}
                checked={checked}
                onCheckedChange={(c) => {
                  const next = c
                    ? [...(value || []), opt.value]
                    : (value || []).filter((v) => v !== opt.value);
                  onChange(next);
                }}
                onSelect={(e) => e.preventDefault()}
              >
                {opt.label}
              </DropdownMenuCheckboxItem>
            );
          })
        )}
      </DropdownMenuContent>
    </DropdownMenu>
  );
}

export { NNSelect };
